import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { JoinCallResponse } from '@doneverse/shared'
import { getNotifier } from '../api/index.js'
import { activeCallOptions, contactsOptions, messagesInfiniteOptions, queryKeys } from '../queries/index.js'
import { callService } from '../services/index.js'

/** The live huddle in a conversation, if any. Drives the joinable pill. */
export function useActiveCall(contactId: string | null | undefined) {
  return useQuery(activeCallOptions(contactId))
}

/**
 * Start a huddle, or join the one already running in this conversation.
 *
 * The response carries a short-lived RealtimeKit `auth_token` — hand it
 * straight to the SDK and do not persist it; a fresh one is minted per join.
 */
export function useJoinCall(opts?: { onSuccess?: (result: JoinCallResponse) => void }) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (contactId: string) => callService.join(contactId),
    onSuccess: (result, contactId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
      // Starting a huddle inserts a message row into this thread server-side.
      // The WS fan-out includes us, but it is best-effort — refetch so our own
      // open thread and contact preview show the card even without a socket.
      void queryClient.invalidateQueries({
        queryKey: messagesInfiniteOptions(contactId).queryKey,
      })
      void queryClient.invalidateQueries({ queryKey: contactsOptions().queryKey })
      opts?.onSuccess?.(result)
    },
    onError: (error) => {
      console.error('Error joining call:', error)
      getNotifier().error('Could not join the call. Please try again.')
    },
  })
}

export function useLeaveCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (callId: string) => callService.leave(callId),
    // Leaving is best-effort from the UI's point of view: the dock is torn down
    // optimistically either way, and the server ends the huddle when the last
    // participant is gone (with the reconciler as the backstop).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
    },
  })
}

/**
 * Turn a call down.
 *
 * Best-effort like leaving: the popup goes away locally either way. The point
 * of the round trip is everything the old client-local dismissal couldn't do —
 * tell the caller, stop the ring on this user's other devices, and stay
 * declined across a reload.
 */
export function useDeclineCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (callId: string) => callService.decline(callId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
    },
  })
}
